/**
 * BoxLookup — search by branch + box number (or by CIF).
 * Uses both `apiClient.accounts.safedepBoxSrch` (/accounts/safedep-box-srch)
 * and `apiClient.jxrl.safeDepBoxSrch` (raw proxy) per api-wiring digest §2.1/§2.2.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, Loader2 } from 'lucide-react';
import {
  DataGrid,
  Button,
  Pill,
  EntityIcon,
} from '@/components/primitives';
import type { DataGridColumn } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { SdbBoxRow } from './types';

interface BoxLookupProps {
  onSelectBox: (box: SdbBoxRow) => void;
}

export function BoxLookup({ onSelectBox: _onSelectBox }: BoxLookupProps): JSX.Element {
  const apiClient = useApiClient();
  const { config } = useConfig();
  const navigate = useNavigate();

  const [branchCode, setBranchCode] = useState<string>('');
  const [boxNumber, setBoxNumber] = useState<string>('');
  const [occupFilter, setOccupFilter] = useState<'UnOccup' | 'Occup' | 'all'>('Occup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ReadonlyArray<SdbBoxRow>>([]);

  const search = async (): Promise<void> => {
    if (!branchCode) {
      setError('Branch is required.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const resp = await apiClient.accounts.safedepBoxSrch({
        BranchCode: branchCode,
        ...(occupFilter !== 'all' ? { BoxOccupType: occupFilter } : {}),
      });
      const boxes = (resp as unknown as {
        Boxes?: Array<{
          AcctId: string;
          BrCode?: string;
          ProdCode?: string;
          ProdDesc?: string;
          RentAmt?: string;
          AcctStat?: string;
        }>;
      }).Boxes ?? [];
      let rows: SdbBoxRow[] = boxes.map((b) => ({
        acctId: b.AcctId,
        branchCode: b.BrCode ?? branchCode,
        boxNumber: b.AcctId,
        boxSize: b.ProdDesc ?? b.ProdCode ?? '',
        rentAmount: Number.parseFloat(b.RentAmt ?? '0') || 0,
        nextDueDate: '',
        balance: 0,
        lateFee: 0,
        keyDeposit: 0,
        keysMissing: false,
        vacant: (b.AcctStat ?? '').toUpperCase().includes('VACANT'),
      }));
      if (boxNumber.trim().length > 0) {
        const needle = boxNumber.trim();
        rows = rows.filter((r) => r.boxNumber.includes(needle));
      }
      setResults(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Box lookup failed.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const columns: DataGridColumn<SdbBoxRow>[] = [
    {
      key: 'box',
      label: 'Box',
      render: (r) => (
        <span className="rsm-cell--acct">
          <EntityIcon type="safedeposit" code="SD" />
          <span>
            <strong>{r.acctId}</strong>
            <div className="rsm-cell--sub">{r.boxSize}</div>
          </span>
        </span>
      ),
    },
    { key: 'branch', label: 'Branch', render: (r) => r.branchCode },
    {
      key: 'rent',
      label: 'Rent',
      numeric: true,
      render: (r) =>
        r.rentAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => (r.vacant ? <Pill variant="ok">Vacant</Pill> : <Pill variant="info">Occupied</Pill>),
    },
  ];

  return (
    <div>
      <div className="rsm-toolbar">
        <div className="rsm-field">
          <label htmlFor="rsm-lookup-branch">Branch</label>
          <select
            id="rsm-lookup-branch"
            value={branchCode}
            onChange={(e) => setBranchCode(e.target.value)}
          >
            <option value="">Select branch…</option>
            {config.branches.map((b) => (
              <option key={b.branchCode} value={b.branchCode}>
                {b.branchName} ({b.branchCode})
              </option>
            ))}
            {config.branches.length === 0 && (
              <>
                <option value="1">Rolla Downtown (1)</option>
                <option value="2">Hwy 72 (2)</option>
                <option value="4">St. James (4)</option>
              </>
            )}
          </select>
        </div>
        <div className="rsm-field">
          <label htmlFor="rsm-lookup-box">Box number (optional)</label>
          <input
            id="rsm-lookup-box"
            type="text"
            value={boxNumber}
            onChange={(e) => setBoxNumber(e.target.value)}
          />
        </div>
        <div className="rsm-field">
          <label htmlFor="rsm-lookup-occup">Occupancy</label>
          <select
            id="rsm-lookup-occup"
            value={occupFilter}
            onChange={(e) => setOccupFilter(e.target.value as 'UnOccup' | 'Occup' | 'all')}
          >
            <option value="Occup">Occupied</option>
            <option value="UnOccup">Vacant</option>
            <option value="all">All</option>
          </select>
        </div>
        <Button
          variant="primary"
          startIcon={loading ? <Loader2 size={14} /> : <Search size={14} />}
          onClick={() => void search()}
          disabled={loading}
        >
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {error && (
        <div className="rsm-banner rsm-banner--err" role="alert">
          {error}
        </div>
      )}

      <DataGrid<SdbBoxRow>
        columns={columns}
        rows={[...results]}
        rowKey={(r) => r.acctId}
        onRowAction={(r) => navigate(`box/${r.acctId}`)}
        emptyMessage="No boxes found. Select a branch and search."
      />
    </div>
  );
}
